import { DeliveryHubPage } from './delivery-hub/DeliveryHubPage'
import { QrPaymentSettingsPage } from './qr-payment/QrPaymentSettingsPage'
import { DeliveryStatisticsPage } from './delivery-statistics/DeliveryStatisticsPage'
import { DeliveryZonesPage } from './delivery-zones/DeliveryZonesPage'
import { DispatchPage } from './dispatch/DispatchPage'
import { ExternalDriverPage } from './external-driver/ExternalDriverPage'
import { DriversRedirect } from './drivers/DriversRedirect'
import { TripLogPage } from './trip-log/TripLogPage'
import { VehicleFleetPage } from './vehicle-fleet/VehicleFleetPage'

/**
 * `manager-delivery` — the road: dispatch, drivers, vehicles and zones.
 *
 * Ported: every page.
 *
 * `external-driver` carried the §9.2 diagnosis: the feature was broken because
 * `is_external` had no write path in either backend, so the screen managed a set
 * that was always empty. The screen itself was correct and was ported faithfully;
 * the fix was a BACKEND.md work item, because §9.2 forbids the frontend papering
 * over a broken API. **The backend gained that write path and this said otherwise
 * until 2026-08-05** — see `external-driver/external-driver.ts` for what changed.
 *
 * The zone arithmetic the editor writes is in `@agos/shared` —
 * `findZoneForPoint`, `deliveryFeeCents`, 16 tests — because the order builder
 * and the dispatch board read the same answers.
 */
export const managerDeliveryModule = {
  name: 'manager-delivery',
  routes: [
    {
      path: '/dispatch',
      page: 'dispatch',
      guard: { permission: 'dispatch' },
      component: DispatchPage,
    },
    {
      // §9.302 - a hub since the owner asked for the QR payment settings to live here.
      //
      // **`anyPermission`, not `permission`.** `delivery_settings` and `qr_payment` are separate
      // flags and nothing grants them together. Gated on the first alone, this route would refuse
      // somebody holding only `qr_payment` while the folded sidebar entry still offered it, which is
      // FB-001's exact shape. Each tab keeps its own gate, so nobody sees a tab they may not.
      path: '/delivery-settings',
      page: 'delivery-settings',
      guard: { anyPermission: ['delivery_settings', 'qr_payment'] },
      component: DeliveryHubPage,
    },
    {
      // Moved here from `manager-invoicing` by §9.302, path unchanged: a hub is not a wall in front
      // of a page somebody has bookmarked, and `qr-payment.spec.ts` drives this path.
      path: '/qr-payment',
      page: 'qr-payment-settings',
      guard: { permission: 'qr_payment' },
      component: QrPaymentSettingsPage,
    },
    {
      // Folded into the statistics screen; legacy kept the path as a redirect.
      path: '/drivers',
      page: 'drivers',
      guard: { permission: 'drivers' },
      redirectTo: '/delivery-statistics',
      component: DriversRedirect,
    },
    {
      path: '/external-driver',
      page: 'external-driver',
      guard: { permission: 'external_driver', franchiseModule: 'external_driver' },
      component: ExternalDriverPage,
    },
    {
      path: '/delivery-statistics',
      page: 'delivery-statistics',
      guard: { permission: 'delivery_statistics', franchiseModule: 'delivery_statistics' },
      component: DeliveryStatisticsPage,
    },
    {
      path: '/delivery-zones',
      page: 'delivery-zones',
      guard: { permission: 'delivery_zones' },
      component: DeliveryZonesPage,
    },
    {
      path: '/trip-log',
      page: 'trip-log',
      guard: { permission: 'trip_log', franchiseModule: 'trip_log' },
      component: TripLogPage,
    },
    {
      path: '/vehicle-fleet',
      page: 'vehicle-fleet',
      guard: { permission: 'vehicle_fleet', franchiseModule: 'vehicle_fleet' },
      component: VehicleFleetPage,
    },
  ],
} as const

export {
  DeliveryHubPage,
  DeliveryStatisticsPage,
  DeliveryZonesPage,
  DispatchPage,
  DriversRedirect,
  ExternalDriverPage,
  QrPaymentSettingsPage,
  TripLogPage,
  VehicleFleetPage,
}

// §9.302 - the app's fold test reads these to assert the tabs admit exactly whoever the sidebar
// entries they replaced admitted.
export { DELIVERY_TABS } from './delivery-hub/delivery-hub'
